import {
    closestCenter,
    pointerWithin,
    type CollisionDetection,
    type DragEndEvent,
    type DragMoveEvent,
} from "@dnd-kit/core";
import { resolveRowDropPosition, RowDropPosition } from "./ViewTable.lib";

// Collision detector used only when positional drop is enabled: prefer the
// droppable the pointer is actually within (so the thin before/after zones of a
// nestable row are reliably hittable), falling back to nearest-center when the
// pointer isn't over any row (e.g. between rows during fast drags).
export const pointerWithinThenClosestCenter: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0
        ? pointerCollisions
        : closestCenter(args);
};

// Live pointer Y = where the drag started (activator event) plus the drag
// delta. Pointer-based so it matches the pointer-within collision detector.
// Returns null when the activator wasn't a mouse event (no reliable pointer).
const getPointerYFromDragEvent = (
    activatorEvent: Event | null,
    deltaY: number,
): number | null => {
    const activatorY =
        activatorEvent instanceof globalThis.MouseEvent
            ? activatorEvent.clientY
            : null;
    return activatorY == null ? null : activatorY + deltaY;
};

export interface ResolvedRowDrop {
    rowId: string;
    position: RowDropPosition;
    hardBlocked: boolean;
}

export interface DropIndicatorState {
    rowId: string;
    position: RowDropPosition;
    isBlocked: boolean;
}

interface RowDropResolutionOptions<TData> {
    getRowDragGroupId?: (row: TData) => string;
    canNestRow?: (row: TData) => boolean;
    canDropOnRow?: (row: TData) => boolean;
}

// Computes where a drop lands relative to the over-row from the dragged row's
// current translated rect. Used by the legacy (non-positional) commit path.
const computeRowDropPositionFromEvent = <TData>(
    event: DragEndEvent | DragMoveEvent,
    rowByIdMap: ReadonlyMap<string, TData>,
    canNestRow?: (row: TData) => boolean,
): RowDropPosition => {
    const { over, activatorEvent, delta } = event;
    if (!over) {
        return "on";
    }
    const overRow = rowByIdMap.get(String(over.id));
    const canNest = overRow && canNestRow ? canNestRow(overRow) : true;
    const pointerY = getPointerYFromDragEvent(activatorEvent, delta.y);
    return resolveRowDropPosition({
        pointerY,
        overTop: over.rect.top,
        overHeight: over.rect.height,
        canNest,
    });
};

// Resolves the row + position a positional drop should land on from a live drag
// event. With the pointer-within collision detector the over-row is always an
// actual row, so the raw over-row + its pointer-derived zone are used directly.
// When that row cannot host a drop (a non-leaf structural section/tier per
// `canDropOnRow`) it is a HARD BLOCK: the row is kept as the target with
// `hardBlocked: true` so the indicator shows the blocked style + not-allowed
// cursor, rather than silently redirecting to a sibling. The shared commit path
// does not itself gate on `hardBlocked` — the consumer's `onRowDrop` re-validates
// and rejects a blocked drop (which is what makes it snap back).
export const resolveRowDropFromEvent = <TData>(
    event: DragEndEvent | DragMoveEvent,
    rowByIdMap: ReadonlyMap<string, TData>,
    options: RowDropResolutionOptions<TData>,
): ResolvedRowDrop | null => {
    const { active, over, activatorEvent, delta } = event;
    if (!over) {
        return null;
    }
    const activeId = String(active.id);
    const rawOverId = String(over.id);
    if (rawOverId === activeId) {
        return null;
    }
    const rawOverRow = rowByIdMap.get(rawOverId);
    const activeRow = rowByIdMap.get(activeId);
    if (!rawOverRow || !activeRow) {
        return null;
    }
    const { getRowDragGroupId, canNestRow, canDropOnRow } = options;
    if (
        getRowDragGroupId &&
        getRowDragGroupId(activeRow) !== getRowDragGroupId(rawOverRow)
    ) {
        return null;
    }
    const canNest = canNestRow ? canNestRow(rawOverRow) : true;
    const pointerY = getPointerYFromDragEvent(activatorEvent, delta.y);
    const rawPosition = resolveRowDropPosition({
        pointerY,
        overTop: over.rect.top,
        overHeight: over.rect.height,
        canNest,
    });
    const hardBlocked = canDropOnRow ? !canDropOnRow(rawOverRow) : false;
    return { rowId: rawOverId, position: rawPosition, hardBlocked };
};

// Builds the next drop-indicator state for a resolved hover. A hover over a row
// that cannot host a drop at all (`hardBlocked`) is always blocked; otherwise
// the consumer's `validateDrop` (when opted in) decides. The committed drop
// still re-validates.
export const buildDropIndicatorState = <TData>({
    resolved,
    activeRow,
    overRow,
    validateDrop,
}: {
    resolved: ResolvedRowDrop;
    activeRow: TData | undefined;
    overRow: TData | undefined;
    validateDrop?: (args: {
        activeRow: TData;
        overRow: TData;
        dropPosition: RowDropPosition;
    }) => boolean;
}): DropIndicatorState => {
    const isBlocked =
        resolved.hardBlocked ||
        (!!validateDrop && !!activeRow && !!overRow
            ? !validateDrop({
                  activeRow,
                  overRow,
                  dropPosition: resolved.position,
              })
            : false);
    return {
        rowId: resolved.rowId,
        position: resolved.position,
        isBlocked,
    };
};

// Resolves the target a drop should commit to: the last shown indicator for
// positional drops (single source of truth so the drop lands where the user saw
// it), otherwise the legacy drag-direction target derived from the event.
export const resolveDropCommitTarget = <TData>({
    isPositionalDropEnabled,
    shownIndicator,
    event,
    rowByIdMap,
    canNestRow,
}: {
    isPositionalDropEnabled: boolean;
    shownIndicator: Pick<DropIndicatorState, "rowId" | "position"> | null;
    event: DragEndEvent;
    rowByIdMap: ReadonlyMap<string, TData>;
    canNestRow?: (row: TData) => boolean;
}): { rowId: string; position: RowDropPosition } | null => {
    if (isPositionalDropEnabled) {
        return shownIndicator;
    }
    const { active, over } = event;
    return over && active.id !== over.id
        ? {
              rowId: String(over.id),
              position: computeRowDropPositionFromEvent(
                  event,
                  rowByIdMap,
                  canNestRow,
              ),
          }
        : null;
};

// The set of ids a positional drag should lift together (a multi-selection or a
// group + descendants). Null unless the consumer opts in and the set has >1
// member, so single-row drags and legacy tables are unaffected.
export const getActiveDragSetIds = <TData>({
    isPositionalDropEnabled,
    activeDragRowId,
    getActiveDragRowIds,
    rowByIdMap,
}: {
    isPositionalDropEnabled: boolean;
    activeDragRowId: string | null;
    getActiveDragRowIds?: (activeRow: TData) => string[];
    rowByIdMap: ReadonlyMap<string, TData>;
}): Set<string> | null => {
    if (!isPositionalDropEnabled || !getActiveDragRowIds || !activeDragRowId) {
        return null;
    }
    const activeRow = rowByIdMap.get(activeDragRowId);
    if (!activeRow) {
        return null;
    }
    const ids = getActiveDragRowIds(activeRow);
    return ids.length > 1 ? new Set(ids) : null;
};
